import { DATAGRAIL_CONSENT_IDS, type DataGrailConsentId } from './constants';
import type { DataGrailConsentCategory } from './types';
import { HOTPADS_COMSCORE_BEACON_SRC } from '@/src/lib/comscore/constants';
import { getHotpadsComscoreBeaconScriptContents } from '@/src/lib/comscore/script';

const FLOODLIGHT_TRACKING_ID = 'DC-8027397';
const BING_UET_TRACKING_ID = '4017789';

export type DataGrailManagedScript = {
  id: string;
  type: 'text/plain';
  category: Exclude<DataGrailConsentCategory, 'essential'>;
  consentId: DataGrailConsentId;
  src?: string;
  async?: boolean;
  contents?: string;
};

export function getFloodlightConfigScriptContents() {
  return `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${FLOODLIGHT_TRACKING_ID}', {'currency': 'USD', 'country': 'US'});
  `;
}

export function getBingUetBootstrapScriptContents() {
  return `
    (function(w,d,t,r,u){
      var f,n,i;
      w[u]=w[u]||[];
      f=function(){
        var o={ti:"${BING_UET_TRACKING_ID}"};
        o.q=w[u];
        w[u]=new UET(o);
        w[u].push("pageLoad");
      };
      n=d.createElement(t);
      n.setAttribute('data-category','marketing');
      n.setAttribute('data-consent-id','${DATAGRAIL_CONSENT_IDS.BING_ADS}');
      n.type='text/plain';
      n.src=r;
      n.async=1;
      n.onload=n.onreadystatechange=function(){
        var s=this.readyState;
        if (!s || s==="loaded" || s==="complete") {
          f();
          n.onload=n.onreadystatechange=null;
        }
      };
      i=d.getElementsByTagName(t)[0];
      i.parentNode.insertBefore(n,i);
    })(window,document,"script","https://bat.bing.com/bat.js","uetq");
  `;
}

export function getDataGrailManagedScripts({
  comscoreConfigScriptContents,
  hasSharedGtagLoader = false,
}: {
  comscoreConfigScriptContents: string;
  /**
   * When the page already loads gtag.js (the GA4 loader), skip the Floodlight
   * copy: both configs queue on the shared window.dataLayer, and the consent
   * boundary is the config script, not the library (HP-8780).
   */
  hasSharedGtagLoader?: boolean;
}): Array<DataGrailManagedScript> {
  const floodlightLoader: Array<DataGrailManagedScript> = hasSharedGtagLoader
    ? []
    : [
        {
          id: 'floodlight-gtag-loader',
          type: 'text/plain',
          category: 'marketing',
          consentId: DATAGRAIL_CONSENT_IDS.FLOODLIGHT_GOOGLE_ANALYTICS,
          src: `https://www.googletagmanager.com/gtag/js?id=${FLOODLIGHT_TRACKING_ID}`,
          async: true,
        },
      ];

  return [
    ...floodlightLoader,
    {
      id: 'floodlight-gtag-config',
      type: 'text/plain',
      category: 'marketing',
      consentId: DATAGRAIL_CONSENT_IDS.FLOODLIGHT_GOOGLE_ANALYTICS,
      contents: getFloodlightConfigScriptContents(),
    },
    {
      id: 'indexTracker',
      type: 'text/plain',
      category: 'marketing',
      consentId: DATAGRAIL_CONSENT_IDS.AD_WRAPPER_INDEX,
      src: 'https://js-sec.indexww.com/ht/p/182276-265378620286299.js',
      async: true,
    },
    {
      id: 'google-ad-services',
      type: 'text/plain',
      category: 'marketing',
      consentId: DATAGRAIL_CONSENT_IDS.GOOGLE_AD_SERVICES,
      src: 'https://www.googleadservices.com/pagead/conversion_async.js',
      async: true,
    },
    {
      id: 'comscore-config-script',
      type: 'text/plain',
      category: 'performance',
      consentId: DATAGRAIL_CONSENT_IDS.COMSCORE,
      contents: comscoreConfigScriptContents,
    },
    {
      id: 'comscore-beacon-script',
      type: 'text/plain',
      category: 'functional',
      consentId: DATAGRAIL_CONSENT_IDS.COMSCORE,
      contents: getHotpadsComscoreBeaconScriptContents(HOTPADS_COMSCORE_BEACON_SRC),
    },
  ];
}
